import { EyeOutlined } from '@ant-design/icons';
import { useGetFrontAreaPubblicaRichieste } from '@framework/api-client/api/inquiries-compile/area-pubblica-osservazioni-controller/area-pubblica-osservazioni-controller.ts';
import { type GetFrontAreaPubblicaRichiesteParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontAreaPubblicaRichiesteParams.ts';
import { type RichiestaPubblicaDto } from '@framework/api-client/api/inquiries-compile/schemas/richiestaPubblicaDto.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { LinkButton } from '@framework/ui/components/button/LinkButton.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { FilterBar } from '@framework/ui/components/search/filter-bar/FilterBar.tsx';
import { TableActionsCellSpace } from '@framework/ui/components/table/TableActionsCellSpace.tsx';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { getPagination } from '@framework/ui/core/pagination/getPagination.ts';
import { handlePaginationChange } from '@framework/ui/core/pagination/handlePaginationChange.ts';
import { getPaginationUrlColumns } from '@framework/ui/core/pagination/paginationUrlColumns.ts';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  generatePath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { Divider } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { returnFocusToPageTitle } from '../../../ui/layout/header/returnFocusToPageTitle.tsx';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../loading/LoadingPage.tsx';
import { GetFrontExtendedFiltersSchema } from '../../submission/schema/GetFrontExtendedFiltersSchema.ts';
import { useObservationSearchableFields } from './filter/useObservationSearchableFields.tsx';
import {
  observationsColumns,
  observationsColumnsMobile,
} from './table/observationsColumns.tsx';

const PAGE_TITLE_ID = 'observation-list-title-focus-id';

export interface IObservationsListPageProps extends RouteComponentProps {}

export function ObservationsListPage(props: IObservationsListPageProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();

  const isMobile = useBreakpoint(down('md')) ?? false;

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        sort: 'progressivo',
        direction: ColumnSortDirections.descend,
      }),
    [],
  );

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('observation.area-pubblica', 'Area Pubblica'),
  });

  const [query, setQuery] =
    usePaginationUrlQueryState<GetFrontAreaPubblicaRichiesteParams>({
      history,
      input: defaultQuery,
    });

  const { data, error, isLoading, isFetching } =
    useGetFrontAreaPubblicaRichieste(query, {
      query: { keepPreviousData: true },
    });

  const observations = data?.dati;
  const meta = data?.meta;

  const defaultColumns = isMobile
    ? observationsColumnsMobile
    : observationsColumns;

  const columns = getPaginationUrlColumns<
    RichiestaPubblicaDto,
    GetFrontAreaPubblicaRichiesteParams
  >(defaultColumns, query);

  const searchableFields = useObservationSearchableFields();

  const onPaginationChange = handlePaginationChange<
    RichiestaPubblicaDto,
    GetFrontAreaPubblicaRichiesteParams
  >({
    columns,
    defaultQuery,
    query,
    setQuery,
    getFilters: filters => ({
      // statoRichiesta: filters
      //   ?.statoRichiesta?.[0] as GetFrontRichiesteStatoRichiesta,
    }),
  });

  const filterBar = (
    <FilterBar
      isFetching={isFetching}
      total={meta?.totale ?? undefined}
      id="observation-list"
      schema={GetFrontExtendedFiltersSchema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      // transform={semanticTagSearchableOptions.transform}
      // extraActions={<ExportSubmissionDropdown query={query} />}
      layout={{ showInCard: true }}
      messageListProps={{
        message: t(
          'observation.domande-trovate-in-base-ai-filtri-di-ricerca',
          'domande trovate in base ai filtri di ricerca.',
        ),
        resetButtonProps: {
          onClick: () => {
            setQuery(defaultQuery);
            returnFocusToPageTitle(PAGE_TITLE_ID);
          },
          children: t('observation.rimuovi-filtri', 'Rimuovi filtri'),
        },
      }}
    />
  );

  if (isLoading ?? isFetching) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'observation.impossibile-caricare-elenco-area-pubblica',
          "Impossibile caricare l'elenco dell'Area Pubblica",
        )}
        error={error}
      />
    );
  }

  return (
    <PageLayout isTablePage>
      <ResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        noPadding
        title={
          <TitleSubmissionRow id={PAGE_TITLE_ID}>
            {t('observation.area-pubblica', 'Area Pubblica')}
          </TitleSubmissionRow>
        }
      >
        {!theme.searchButtonOnTableFlag && <Divider style={{ margin: 0 }} />}

        <TableData
          title={() => <div>{filterBar}</div>}
          darkTitleBar={false}
          showSorterTooltip={false}
          size="small"
          dataSource={observations ?? []}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={[
            ...columns,
            {
              title: 'Azioni',
              key: 'actions',
              align: 'right',
              render: (text, record) => (
                <TableActionsCellSpace>
                  <LinkButton
                    aria-label={`Vai alla richiesta ${
                      record.richiesta?.progressivo ?? ''
                    }`}
                    type="link"
                    size="large"
                    icon={<EyeOutlined aria-hidden />}
                    disabled={!record.richiesta?.id}
                    to={generatePath('/area-pubblica/:id', {
                      id: record.richiesta?.id ?? '',
                    })}
                  />
                </TableActionsCellSpace>
              ),
            },
          ]}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
